import React from 'react';
import api from 'api';
import {useGetApi} from 'common/hooks/useGetApi';
import {AdminRoles, IAdminRoles} from 'types/diplomat/settings/adminroles/AdminRoles';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {PermissionsFormWrapper} from 'features/layout/DialogRoutes/dialogs/settings/PermissionsDialog/PermissionsFormWrapper';
import AsyncContainer from 'components/AsyncContainer';
import {IDiplomatFolder} from 'types/DiplomatFolder';
import {useToasts} from 'features/notifications/useToasts';
import {IDiplomatObjectPermission} from 'types/diplomat/settings/permissions/DiplomatObjectPermission';
import {map} from 'lodash';
import {AdminRole, IAdminRole} from 'types/diplomat/settings/adminroles/AdminRole';
import useDictionary from 'common/hooks/useDictionary';
import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {usePostArrayApi} from 'common/hooks/usePostArrayApi';
import {useDispatch} from 'react-redux';
import {loginActions} from 'features/login/redux';

const usePermissionsAsRoles = (objectType: string, id: string) => {
   const {request, state, loading} = useGetApi<IDiplomatObjectPermission>(
      api.permissions + '/' + objectType + '/' + id
   );
   React.useEffect(request, [request]);

   const {state: adminUsers} = useDictionary<IDiplomatAdminUser[]>(api.adminUsers);
   const {state: adminRoles} = useDictionary<IAdminRole[]>(api.adminroles);

   console.debug('PermissionsForm State', objectType, id, state);

   const roles = React.useMemo(() => {
      if (!state || !adminRoles || !adminUsers) return;
      const result = map(state.roleAccesses, (r, id) =>
         AdminRole.cast({
            id,
            name: adminRoles?.find((r) => r.id === id)?.name,
            objectType: DiplomatObjectType.enum.ADMIN_ROLE,
            resources: r.aces,
         })
      );

      Array.prototype.push.apply(
         result,
         map(state.userAccesses, (r, id) => {
            const user = adminUsers?.find((r) => r.id === id);
            return AdminRole.cast({
               id,
               name: user?.name || user?.userName || `(User-${id})`,
               objectType: DiplomatObjectType.enum.USER,
               resources: r.aces,
            });
         })
      );

      return result;
   }, [state, adminRoles, adminUsers]);

   console.debug('PermissionsForm state roles', roles);

   return {
      roles,
      refetch: request,
      loading,
   };
};

type Props = {
   onClose: (result?: string) => void;
   data: IDiplomatFolder;
};
export const PermissionsForm = ({onClose, data}: Props) => {
   const {addToast} = useToasts();

   const {refetch, roles, loading} = usePermissionsAsRoles(data.objectType, data._id || data.id);
   const dispatch = useDispatch();

   const {request: updateRoles} = usePostArrayApi(api.permissions);
   const onSubmit = React.useCallback(
      async (roles: IAdminRoles, helpers: FormOnSubmitHelpers<IAdminRoles>) => {
         console.debug('submit roles', roles);
         const bodies = roles.roles.map((r) =>
            JSON.stringify({
               objectType: data.objectType,
               resourceId: data._id || data.id,
               userId: r.objectType === DiplomatObjectType.enum.USER ? r.id : undefined,
               roleId: r.objectType === DiplomatObjectType.enum.ADMIN_ROLE ? r.id : undefined,
               ace: {
                  aces: r.resources,
               },
            })
         );
         await updateRoles(bodies, {
            onSuccess: () => {
               helpers.setSubmitting(false);
               addToast('Permissions succesfully updated', {appearance: 'success'});
               refetch();
               dispatch(
                  loginActions.clearPermissionsEntry(`${data.objectType}/${data._id || data.id}`)
               );
            },
         });
      },
      [updateRoles, addToast, refetch]
   );

   return (
      <AsyncContainer loading={loading}>
         <Formik onSubmit={onSubmit} initialValues={AdminRoles.cast({roles})} enableReinitialize>
            <PermissionsFormWrapper refetch={refetch} onClose={onClose} data={data} />
         </Formik>
      </AsyncContainer>
   );
};
